import { useCallback, useMemo } from 'react';
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query';
import { KNOWLEDGE_RESOURCES_TYPES } from '../constants/knowledge.constants';
import {
  SCHEDULE_EVENT_TYPES,
  EDUCATIONAL_EVENTS_PAST_DAYS,
  EDUCATIONAL_EVENTS_PAST_PAGE_SIZE,
  EDUCATIONAL_EVENTS_FUTURE_PAGE_SIZE,
} from '../constants/schedule.constants';
import scheduleServiceApi from '../api/schedule.service.api';
import { scheduleEventKeys, scheduleKeys, nextEventsPage } from './schedule.keys';
import { startOfToday } from '../utils/date.utils';
import { pastEventsWindow } from '../utils/schedule-window';
import { useSelector } from 'react-redux';
import { type QueryOpts } from '../api/query-client';
import { isScheduleCasesEnabled } from '../store/selectors/features.selector';
import type { CheckInStatus } from './attendance.types';

type KnowledgeResourceType =
  (typeof KNOWLEDGE_RESOURCES_TYPES)[keyof typeof KNOWLEDGE_RESOURCES_TYPES];
type ExternalUrlType = typeof KNOWLEDGE_RESOURCES_TYPES.EXTERNAL_URL;

export type ResourceInput =
  | { type: ExternalUrlType; title: string; url: string }
  | {
      type: Exclude<KnowledgeResourceType, ExternalUrlType>;
      id: string;
      title?: string;
      url?: string;
    };

interface StoredResource {
  id: string;
  type: string;
  title: string;
  url?: string;
  createdBy?: string;
  createdAt?: string;
}

export interface ScheduleEvent {
  id: string;
  title?: string;
  eventType?: string;
  attributes?: { knowledgeItems?: StoredResource[]; [key: string]: unknown };
  checkIn?: CheckInStatus;
  children?: ScheduleEvent[];
  [key: string]: unknown;
}

interface ScheduleEventDetailOptions extends QueryOpts<ScheduleEvent> {
  // Honoured only when the org's schedule-cases flag is on; the gate lives here, not in callers.
  includeChildren?: boolean;
}

interface DateRange {
  from?: string;
  until?: string;
}

/** Rows stay loosely typed (JS consumers); the container shape is what callers rely on. */
interface AttendanceReport {
  items: unknown[];
  total?: number;
}

interface PersonAttendance {
  items: unknown[];
  user?: Record<string, unknown>;
}

export { scheduleEventKeys } from './schedule.keys';

export const withEventResources = (
  prev: ScheduleEvent | undefined,
  resources: StoredResource[],
): ScheduleEvent | undefined =>
  prev ? { ...prev, attributes: { ...prev.attributes, knowledgeItems: resources } } : prev;

export const withoutEventResource = (
  prev: ScheduleEvent | undefined,
  resourceId: string,
): ScheduleEvent | undefined =>
  prev
    ? withEventResources(
        prev,
        (prev.attributes?.knowledgeItems ?? []).filter(k => k.id !== resourceId),
      )
    : prev;

/**
 * Updates every cached detail of an event, with or without its children. `setQueryData` matches
 * one key exactly, so it would miss the `includeChildren` variant; this matches by prefix.
 *
 * @param qc - The query client.
 * @param eventId - The event whose cached details change.
 * @param updater - Receives each cached detail (undefined when absent) and returns the new one.
 * @returns Nothing.
 * @example
 * updateEventDetailCache(qc, eventId, prev => withoutEventResource(prev, resourceId));
 */
export const updateEventDetailCache = (
  qc: QueryClient,
  eventId: string,
  updater: (prev: ScheduleEvent | undefined) => ScheduleEvent | undefined,
) => qc.setQueriesData<ScheduleEvent>({ queryKey: scheduleEventKeys.detail(eventId) }, updater);

const setEventResources = (qc: QueryClient, eventId: string, resources: StoredResource[]) =>
  updateEventDetailCache(qc, eventId, prev => withEventResources(prev, resources));

/**
 * An event's detail. With `includeChildren`, a parent assignment also carries its live CASE
 * `children`, but only while the `schedule-cases` flag is on; otherwise the plain detail is read.
 * The two shapes have separate cache keys.
 *
 * @param id - The event id; the query waits while it is missing.
 * @param options - `includeChildren` (default `false`), plus any react-query option, such as
 *   `initialData`.
 * @returns The react-query result for the event.
 * @example
 * const { data, refetch } = useScheduleEventDetail(eventId, { includeChildren: true, initialData: routeEvent });
 */
export function useScheduleEventDetail(
  id: string | null | undefined,
  { includeChildren = false, ...options }: ScheduleEventDetailOptions = {},
) {
  const casesEnabled = useSelector(isScheduleCasesEnabled) as boolean;
  const withChildren = includeChildren && casesEnabled;
  return useQuery<ScheduleEvent>({
    queryKey: scheduleEventKeys.detail(id, { includeChildren: withChildren }),
    // `enabled` already guards a missing id.
    queryFn: () =>
      scheduleServiceApi.getScheduleEvent({
        id: id as string,
        includeChildren: withChildren,
      }) as Promise<ScheduleEvent>,
    enabled: !!id,
    ...options,
  });
}

interface EventsPage {
  events: ScheduleEvent[];
  total: number;
}

/**
 * Educational events for the events sidebar: a bounded past window (started before
 * today) in one request, plus today-and-future events paginated — `loadMore`
 * fetches the next future page. Returned ascending.
 */
export function useEventsList() {
  const pastQuery = useQuery<ScheduleEvent[]>({
    queryKey: scheduleEventKeys.listPast(),
    queryFn: async () => {
      const params: Record<string, unknown> = {
        eventType: [SCHEDULE_EVENT_TYPES.EDUCATIONAL],
        ...pastEventsWindow(EDUCATIONAL_EVENTS_PAST_DAYS),
        sortOrder: 'desc',
        pageSize: EDUCATIONAL_EVENTS_PAST_PAGE_SIZE,
      };
      const { events } = (await scheduleServiceApi.getScheduleEvents(params)) as {
        events?: ScheduleEvent[];
      };
      return events ?? [];
    },
  });

  const futureQuery = useInfiniteQuery<EventsPage>({
    queryKey: scheduleEventKeys.listFuture(),
    queryFn: ({ pageParam }) => {
      const params: Record<string, unknown> = {
        eventType: [SCHEDULE_EVENT_TYPES.EDUCATIONAL],
        startTime: startOfToday().toISOString(),
        sortOrder: 'asc',
        page: pageParam,
        pageSize: EDUCATIONAL_EVENTS_FUTURE_PAGE_SIZE,
      };
      return scheduleServiceApi.getScheduleEvents(params) as Promise<EventsPage>;
    },
    initialPageParam: 1,
    getNextPageParam: nextEventsPage,
  });

  const events = useMemo(() => {
    const past = pastQuery.data ?? [];
    const future = (futureQuery.data?.pages ?? []).flatMap(page => page.events ?? []);
    return [...[...past].reverse(), ...future];
  }, [pastQuery.data, futureQuery.data]);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = futureQuery;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    events,
    loading: pastQuery.isPending || futureQuery.isPending,
    // A failed load-more must not blank an already-rendered list — page-level
    // error only when nothing loaded at all.
    error: events.length === 0 ? (pastQuery.error ?? futureQuery.error ?? null) : null,
    loadMoreError: futureQuery.isFetchNextPageError ? futureQuery.error : null,
    hasMore: hasNextPage ?? false,
    loadingMore: isFetchingNextPage,
    loadMore,
  };
}

export function useAddEventResources() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, resources }: { eventId: string; resources: ResourceInput[] }) =>
      scheduleServiceApi.addEventResources({ eventId, resources }) as Promise<{
        resources: StoredResource[];
      }>,
    onSuccess: (data, { eventId }) => {
      if (Array.isArray(data?.resources)) setEventResources(qc, eventId, data.resources);
    },
  });
}

export function useRemoveEventResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, resourceId }: { eventId: string; resourceId: string }) =>
      scheduleServiceApi.removeEventResource({ eventId, resourceId }),
    onSuccess: (_data, { eventId, resourceId }) => {
      updateEventDetailCache(qc, eventId, prev => withoutEventResource(prev, resourceId));
    },
  });
}

export function useAddEventGroupResources() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, resources }: { groupId: string; resources: ResourceInput[] }) =>
      scheduleServiceApi.addEventGroupResources({ groupId, resources }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduleEventKeys.all });
    },
  });
}

export function useRemoveEventGroupResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, resourceId }: { groupId: string; resourceId: string }) =>
      scheduleServiceApi.removeEventGroupResource({ groupId, resourceId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduleEventKeys.all });
    },
  });
}

interface EventGroupCreateBody {
  type: string;
  name: string;
  details: Record<string, unknown>;
}

export function useCreateEventGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EventGroupCreateBody) => scheduleServiceApi.createEventGroup(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduleEventKeys.all });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

interface EventGroupUpdateArgs {
  groupId: string;
  patch: { name?: string; details?: Record<string, unknown> };
}

export function useUpdateEventGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, patch }: EventGroupUpdateArgs) =>
      scheduleServiceApi.updateEventGroup({ groupId, patch }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduleEventKeys.all });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useDeleteEventGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId }: { groupId: string }) =>
      scheduleServiceApi.deleteEventGroup({ groupId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduleEventKeys.all });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useByPersonReport(filters: DateRange) {
  return useQuery<AttendanceReport>({
    queryKey: scheduleEventKeys.reportByPerson(filters),
    placeholderData: keepPreviousData,
    queryFn: () =>
      scheduleServiceApi.getAttendanceReportByPerson(filters) as Promise<AttendanceReport>,
  });
}

export function usePersonAttendance(userId: string | null | undefined, range: DateRange) {
  return useQuery<PersonAttendance>({
    queryKey: scheduleEventKeys.reportPerson(userId, range),
    enabled: !!userId,
    placeholderData: keepPreviousData,
    queryFn: () =>
      scheduleServiceApi.getPersonAttendance({
        userId: userId as string,
        ...range,
      }) as Promise<PersonAttendance>,
  });
}

export function useByEventReport(filters: DateRange) {
  return useQuery<AttendanceReport>({
    queryKey: scheduleEventKeys.reportByEvent(filters),
    placeholderData: keepPreviousData,
    queryFn: () =>
      scheduleServiceApi.getAttendanceReportByEvent(filters) as Promise<AttendanceReport>,
  });
}
